import React from 'react';
import { Box, Typography } from '@mui/material';
import LoadingState from './LoadingState';
import {
  FullCapsule,
  KeyInsights,
  MindMap,
  AiChat,
  Mcqs,
  Faqs,
  FlashCards,
  StudyNotes,
  Terminology
} from './outputs';
import { ContentCategory, OutputLanguage } from '../services/contentService';
// Output format types
export type OutputFormat = 
  'fullCapsule' | 
  'keyInsights' | 
  'mindMap' | 
  'aiChat' | 
  'mcqs' | 
  'faqs' | 
  'flashCards' | 
  'studyNotes' | 
  'terminology';

interface ContentDisplayProps {
  outputFormat: OutputFormat;
  videoId: string;
  videoTitle: string;
  videoDuration: string;
  category: ContentCategory;
  language: OutputLanguage;
  isLoading?: boolean;
  error?: string | null;
  content: any; // The content will vary based on the output format
  isSaved?: boolean;
  onSave?: () => void;
  onCopy?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
  onChatQuestion?: (question: string) => void;
}

const ContentDisplay: React.FC<ContentDisplayProps> = ({
  outputFormat,
  videoId,
  videoTitle,
  videoDuration,
  category,
  language,
  isLoading = false,
  error = null,
  content,
  isSaved = false,
  onSave = () => {},
  onCopy = () => {},
  onShare = () => {},
  onDownload = () => {},
  onPrint = () => {},
  onChatQuestion = () => {}
}) => {
  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        <LoadingState
          videoTitle={videoTitle}
          outputFormat={getOutputFormatName(outputFormat)}
        />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Error generating content
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
      </Box>
    );
  }

  // No content state
  if (!content) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No content available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please select a different output format or try again.
        </Typography>
      </Box>
    );
  }

  // Render the appropriate component based on the output format
  switch (outputFormat) {
    case 'fullCapsule':
      // Extract the content from the response
      let fullCapsuleContent = '';
      
      // Check if content is a string or an object
      if (typeof content === 'string') {
        fullCapsuleContent = content;
      } else if (content && typeof content.content === 'string') {
        // If content is an object with a content property that is a string
        fullCapsuleContent = content.content;
      } else if (content && content.fullCapsule && typeof content.fullCapsule === 'string') {
        // If content has a fullCapsule property that is a string
        fullCapsuleContent = content.fullCapsule;
      } else {
        // Try to extract content from JSON
        try {
          // If content is an object, try to stringify it
          if (content && content.content) {
            const jsonMatch = content.content.match(/```json\n([\s\S]*?)\n```/) ||
                             content.content.match(/```\n([\s\S]*?)\n```/) ||
                             content.content.match(/{[\s\S]*}/);
            
            if (jsonMatch) {
              const jsonContent = jsonMatch[1] || jsonMatch[0];
              try {
                const parsedJson = JSON.parse(jsonContent);
                if (parsedJson && parsedJson.fullCapsule) {
                  fullCapsuleContent = parsedJson.fullCapsule;
                } else {
                  fullCapsuleContent = jsonContent;
                }
              } catch (error) {
                console.error('Error parsing JSON:', error);
                fullCapsuleContent = content.content;
              }
            } else {
              fullCapsuleContent = content.content;
            }
          } else if (content && typeof content === 'object') {
            // If content is an object but doesn't have a content property
            // Try to extract any string properties that might contain the content
            const possibleContentProps = ['fullCapsule', 'content', 'text', 'data', 'result', 'output'];
            for (const prop of possibleContentProps) {
              if (content[prop] && typeof content[prop] === 'string') {
                fullCapsuleContent = content[prop];
                break;
              }
            }
            
            // If we still don't have content, try to stringify the object
            if (!fullCapsuleContent) {
              try {
                fullCapsuleContent = JSON.stringify(content, null, 2);
              } catch (e) {
                fullCapsuleContent = 'Error: Could not display content. Please try again.';
              }
            }
          } else {
            fullCapsuleContent = 'Error: Could not display content. Please try again.';
          }
        } catch (error) {
          console.error('Error processing content:', error);
          fullCapsuleContent = 'Error: Could not display content. Please try again.';
        }
      }
      
      // Process all videos consistently - AGGRESSIVE CLEANUP: Extract only the numbered points
      try {
        // First, remove any JSON or other formatting that might be in the content
        fullCapsuleContent = fullCapsuleContent
          .replace(/```json|```/g, '')
          .replace(/^\s*\{\s*"fullCapsule"\s*:\s*"/, '')
          .replace(/"\s*\}\s*$/, '');
        
        // Extract all numbered points
        const numberedPointsRegex = /\d+\.\s*[^\n]+/g;
        const matches = fullCapsuleContent.match(numberedPointsRegex);
        
        if (matches && matches.length > 0) {
          // Filter out empty points (points that are just quotes or empty)
          const validPoints = matches.filter(point => {
            const pointContent = point.replace(/^\d+\.\s*/, '').trim();
            return pointContent !== '' && pointContent !== '"' && pointContent !== '",';
          });
          
          // Join all valid numbered points with newlines
          fullCapsuleContent = validPoints.join('\n\n');
          console.log(`Extracted ${validPoints.length} valid numbered points`);
        } else {
          console.log('No numbered points found, using original content');
        }
      
        // Remove any remaining JSON artifacts
        fullCapsuleContent = fullCapsuleContent
          .replace(/",\s*"keyInsights"[\s\S]*$/, '') // Remove everything after fullCapsule
          .replace(/\\n/g, '\n') // Replace escaped newlines
          .replace(/\\"/g, '"') // Replace escaped quotes
          .replace(/\\\\/g, '\\') // Replace escaped backslashes
          .replace(/\n\d+\.\s*\n/g, '\n') // Remove empty numbered points
          .replace(/\n{3,}/g, '\n\n'); // Replace multiple newlines with double newlines
      } catch (error) {
        console.error('Error cleaning content:', error);
      }
      
      return (
        <FullCapsule
          content={fullCapsuleContent}
          timeSaved={content.timeSaved || 5}
          videoTitle={videoTitle}
          isSaved={isSaved}
          onSave={onSave}
          onCopy={onCopy}
          onShare={onShare}
          onDownload={onDownload}
          onPrint={onPrint}
        />
      );
    
    case 'keyInsights':
      return (
        <KeyInsights
          insights={content.insights || []}
          timeSaved={content.timeSaved}
          videoTitle={videoTitle}
          isSaved={isSaved}
          onSave={onSave}
          onCopy={onCopy}
          onShare={onShare}
          onDownload={onDownload}
          onPrint={onPrint}
        />
      );
    
    case 'mindMap':
      return (
        <MindMap
          data={content}
          timeSaved={10} // Placeholder, should be calculated based on video duration
          videoTitle={videoTitle}
          isSaved={isSaved}
          onSave={onSave}
          onShare={onShare}
          onDownload={onDownload}
          onPrint={onPrint}
        />
      );
    
    case 'aiChat':
      return (
        <AiChat
          videoTitle={videoTitle}
          timeSaved={10} // Placeholder, should be calculated based on video duration
          onAskQuestion={async (question, detailLevel) => {
            if (onChatQuestion) {
              onChatQuestion(question);
            }
            // This is a placeholder implementation
            return `This is a placeholder response to your question: "${question}" with detail level: ${detailLevel}`;
          }}
          onGetSuggestedQuestions={async () => {
            // This is a placeholder implementation
            return [
              "What are the main points of the video?",
              "Can you explain the concept mentioned at 2:30?",
              "How does this relate to the topic discussed earlier?"
            ];
          }}
        />
      );
    
    case 'mcqs':
      return (
        <Mcqs
          mcqs={content}
          timeSaved={10} // Placeholder, should be calculated based on video duration
          videoTitle={videoTitle}
          isSaved={isSaved}
          onSave={onSave}
          onCopy={onCopy}
          onShare={onShare}
          onDownload={onDownload}
          onPrint={onPrint}
        />
      );
    
    case 'faqs':
      return (
        <Faqs
          faqs={content}
          timeSaved={10} // Placeholder, should be calculated based on video duration
          videoTitle={videoTitle}
          isSaved={isSaved}
          onSave={onSave}
          onCopy={onCopy}
          onShare={onShare}
          onDownload={onDownload}
          onPrint={onPrint}
        />
      );
    
    case 'flashCards':
      return (
        <FlashCards
          flashCards={content}
          timeSaved={10} // Placeholder, should be calculated based on video duration
          videoTitle={videoTitle}
          isSaved={isSaved}
          onSave={onSave}
          onCopy={onCopy}
          onShare={onShare}
          onDownload={onDownload}
          onPrint={onPrint}
        />
      );
    
    case 'studyNotes':
      return (
        <StudyNotes
          content={content}
          timeSaved={content.timeSaved || 10} // Use provided timeSaved or placeholder
          videoTitle={videoTitle}
          isSaved={isSaved}
          onSave={onSave}
          onCopy={onCopy}
          onShare={onShare}
          onDownload={onDownload}
          onPrint={onPrint}
        />
      );
    
    case 'terminology':
      return (
        <Terminology
          terms={content}
          timeSaved={10} // Placeholder, should be calculated based on video duration
          videoTitle={videoTitle}
          isSaved={isSaved}
          onSave={onSave}
          onCopy={onCopy}
          onShare={onShare}
          onDownload={onDownload}
          onPrint={onPrint}
        />
      );
    
    default:
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Unknown output format
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please select a different output format.
          </Typography>
        </Box>
      );
  }
};

// Helper function to get a human-readable name for the output format
export const getOutputFormatName = (format: OutputFormat): string => {
  const names: Record<OutputFormat, string> = {
    fullCapsule: 'Full Capsule',
    keyInsights: 'Key Insights',
    mindMap: 'Mind Map',
    aiChat: 'AI Chat',
    mcqs: 'Multiple Choice Questions',
    faqs: 'Frequently Asked Questions',
    flashCards: 'Flash Cards',
    studyNotes: 'Study Notes',
    terminology: 'Terminology Simplifier'
  };
  
  return names[format] || 'Content';
};

// Helper function to get an icon name for the output format (for use with dynamic imports)
export const getOutputFormatIcon = (format: OutputFormat): string => {
  const icons: Record<OutputFormat, string> = {
    fullCapsule: 'Article',
    keyInsights: 'Lightbulb',
    mindMap: 'AccountTree',
    aiChat: 'Chat',
    mcqs: 'Quiz',
    faqs: 'Help',
    flashCards: 'ViewAgenda',
    studyNotes: 'MenuBook',
    terminology: 'Translate'
  };
  
  return icons[format] || 'Description';
};

export default ContentDisplay;
